'use client';

import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';

/**
 * A number input that keeps what the shopper is typing.
 *
 * <p>A plain controlled `<input type=number>` bound to state rewrites the field
 * on every keystroke: clearing it to type a new size shows "0", and a
 * half-typed "4" is refitted before the "97" arrives. This keeps the raw text
 * while the field has focus, commits each parseable value as it is typed (so the
 * preview follows along), and shows the committed value again on blur.
 */
export function NumberField({
  value,
  onCommit,
  suffix,
  step,
  ...rest
}: {
  value: number;
  onCommit(value: number): void;
  suffix?: string;
  step?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'step'>) {
  const [draft, setDraft] = useState(String(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setDraft(String(value));
  }, [value]);

  const input = (
    <input
      {...rest}
      className={rest.className ?? 'sk-db__input'}
      type="number"
      inputMode={step && step % 1 !== 0 ? 'decimal' : 'numeric'}
      step={step}
      value={draft}
      onFocus={(e) => {
        focused.current = true;
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        focused.current = false;
        setDraft(String(value));
        rest.onBlur?.(e);
      }}
      onChange={(e) => {
        setDraft(e.target.value);
        const parsed = Number(e.target.value);
        if (e.target.value.trim() !== '' && Number.isFinite(parsed)) onCommit(parsed);
      }}
    />
  );
  if (!suffix) return input;
  return (
    <div className="sk-db__suffix">
      {input}
      <span aria-hidden="true">{suffix}</span>
    </div>
  );
}
